import React, { useEffect, useState } from "react";
import { Menu, X, PhoneCall } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_appointments-engine/artifacts/w44rfe3v_logo.png";

const NAV_LINKS = [
  { href: "#services", label: "Services" },
  { href: "#why-us", label: "Why Us" },
  { href: "#how-it-works", label: "Process" },
  { href: "#performance", label: "Performance" },
  { href: "#contact", label: "Contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 24);
    handler();
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const go = (hash) => (e) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(hash)(e);
  };

  return (
    <header
      data-testid="site-navbar"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        transition: "all 0.5s",
        background: scrolled ? "rgba(8,8,8,0.85)" : "transparent",
        backdropFilter: scrolled ? "blur(20px)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(20px)" : "none",
        borderBottom: scrolled ? "1px solid rgba(210,170,60,0.1)" : "none",
      }}
    >
      <nav
        style={{
          maxWidth: "1280px",
          margin: "0 auto",
          padding: "0 24px",
          height: "76px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <a
          href="#top"
          onClick={go("#top")}
          data-testid="nav-logo"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            textDecoration: "none",
          }}
        >
          <img
            src={LOGO_URL}
            alt="TMN Resource Solutions"
            style={{
              width: "44px",
              height: "44px",
              objectFit: "contain",
            }}
          />
          <div style={{ display: "flex", flexDirection: "column", lineHeight: "1" }}>
            <span
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "16px",
                fontWeight: 500,
                letterSpacing: "0.02em",
                color: "#fff",
              }}
            >
              TMN{" "}
              <span style={{ color: "#e8c45a", fontStyle: "italic" }}>Resource Solutions</span>
            </span>
            <span
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "9px",
                letterSpacing: "0.3em",
                color: "rgba(200,160,50,0.5)",
                textTransform: "uppercase",
                marginTop: "3px",
              }}
            >
              Connecting Vision & Success
            </span>
          </div>
        </a>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "32px",
          }}
          className="hidden lg:flex"
        >
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={go(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "11px",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.55)",
                textDecoration: "none",
                transition: "color 0.25s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#e8c45a")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.55)")}
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden md:block">
          <button
            data-testid="nav-book-button"
            onClick={go("#booking")}
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "10px",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontWeight: 400,
              padding: "12px 28px",
              background: "rgba(210,165,40,0.15)",
              border: "1px solid rgba(210,165,40,0.7)",
              color: "#e8c45a",
              cursor: "pointer",
              transition: "all 0.35s",
              borderRadius: "0",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(210,165,40,0.3)";
              e.currentTarget.style.borderColor = "#e8c45a";
              e.currentTarget.style.color = "#fff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(210,165,40,0.15)";
              e.currentTarget.style.borderColor = "rgba(210,165,40,0.7)";
              e.currentTarget.style.color = "#e8c45a";
            }}
          >
            <PhoneCall style={{ width: "14px", height: "14px" }} />
            Book Strategy Call
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          aria-expanded={open}
          aria-controls="mobile-menu"
          style={{
            background: "none",
            border: "none",
            color: "#fff",
            cursor: "pointer",
            padding: "8px",
          }}
          className="md:hidden"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X style={{ width: "24px", height: "24px" }} /> : <Menu style={{ width: "24px", height: "24px" }} />}
        </button>
      </nav>

      {open && (
        <div
          id="mobile-menu"
          data-testid="nav-mobile-menu"
          style={{
            background: "rgba(8,8,8,0.95)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderTop: "1px solid rgba(255,255,255,0.05)",
          }}
          className="md:hidden"
        >
          <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "20px" }}>
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={go(l.href)}
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: "12px",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.65)",
                  textDecoration: "none",
                  transition: "color 0.25s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#e8c45a")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.65)")}
              >
                {l.label}
              </a>
            ))}
            <button
              data-testid="nav-mobile-book-button"
              onClick={go("#booking")}
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "10px",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                fontWeight: 400,
                padding: "12px 28px",
                background: "rgba(210,165,40,0.15)",
                border: "1px solid rgba(210,165,40,0.7)",
                color: "#e8c45a",
                cursor: "pointer",
                transition: "all 0.35s",
                borderRadius: "0",
              }}
            >
              Book Strategy Call
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
